(() => {
  const ROOT_SELECTOR = '#CartDrawer-ShippingEstimator';
  const STORAGE_KEY = 'cartDrawerSelectedShipping';

  function getRoot(node = document) {
    return node.querySelector(ROOT_SELECTOR);
  }

  function getCurrency(root) {
    return root?.dataset?.currency || 'USD';
  }

  function formatMoneyFromCents(cents, currencyCode) {
    const locale = document.documentElement.lang || 'en';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
      }).format((Number(cents) || 0) / 100);
    } catch (error) {
      return `${((Number(cents) || 0) / 100).toFixed(2)} ${currencyCode}`;
    }
  }

  function toCents(value) {
    return Math.round((Number(value) || 0) * 100);
  }

  function currentSignature(root) {
    return root?.dataset?.cartSignature || '';
  }

  function baseCartTotalCents(root) {
    return Number(root?.dataset?.cartTotalCents || 0);
  }

  function readStoredSelection() {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
    } catch (error) {
      return null;
    }
  }

  function writeStoredSelection(selection) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
  }

  function clearStoredSelection() {
    sessionStorage.removeItem(STORAGE_KEY);
  }

  function setStatus(root, message, isError = false) {
    const status = root.querySelector('#CartDrawerShippingStatus');
    if (!status) return;

    status.textContent = message || '';
    status.classList.toggle('cart-shipping-estimator__status--error', isError);
  }

  function clearResults(root) {
    const results = root.querySelector('#CartDrawerShippingResults');
    if (!results) return;

    results.innerHTML = '';
    results.hidden = true;
  }

  function updateEstimatedTotal(root, shippingCents = 0) {
    const totalEl = document.querySelector('#CartDrawerEstimatedTotalValue');
    if (!totalEl) return;

    const total = baseCartTotalCents(root) + Number(shippingCents || 0);
    totalEl.textContent = formatMoneyFromCents(total, getCurrency(root));
  }

  function showSelectedShipping(root, selection) {
    const row = document.querySelector('#CartDrawerSelectedShippingRow');
    const priceEl = document.querySelector('#CartDrawerSelectedShippingPrice');

    if (!row || !priceEl) return;

    if (!selection) {
      row.hidden = true;
      priceEl.textContent = '';
      return;
    }

    row.hidden = false;
    priceEl.textContent =
      selection.priceCents === 0
        ? 'Free'
        : formatMoneyFromCents(selection.priceCents, getCurrency(root));
  }

  function clearSelection(root, clearStorage = true) {
    if (clearStorage) clearStoredSelection();
    showSelectedShipping(root, null);
    updateEstimatedTotal(root, 0);

    const checked = root.querySelector('input[name="CartDrawerShippingRate"]:checked');
    if (checked) checked.checked = false;
  }

  function selectionMatchesRate(selection, rate, root) {
    if (!selection) return false;

    const name = rate.presentment_name || rate.name || 'Shipping';
    const priceCents = toCents(rate.price);

    return (
      selection.signature === currentSignature(root) &&
      selection.name === name &&
      Number(selection.priceCents) === priceCents
    );
  }

  function setDefaultCountry(root) {
    const countrySelect = root.querySelector('#CartDrawerShippingCountry');
    const defaultCountry = root.dataset.defaultCountry;

    if (!countrySelect || !defaultCountry) return;

    const option = Array.from(countrySelect.options).find((opt) => {
      return opt.value === defaultCountry || opt.text.trim() === defaultCountry;
    });

    if (option) countrySelect.value = option.value;
  }

  function renderProvinceOptions(root) {
    const countrySelect = root.querySelector('#CartDrawerShippingCountry');
    const provinceWrap = root.querySelector('#CartDrawerShippingProvinceWrap');
    const provinceSelect = root.querySelector('#CartDrawerShippingProvince');

    if (!countrySelect || !provinceWrap || !provinceSelect) return;

    const selectedOption = countrySelect.options[countrySelect.selectedIndex];
    let provinces = [];

    try {
      provinces = JSON.parse(selectedOption?.dataset?.provinces || '[]');
    } catch (error) {
      provinces = [];
    }

    provinceSelect.innerHTML = '';

    if (!provinces.length) {
      provinceWrap.hidden = true;
      return;
    }

    provinceWrap.hidden = false;
    provinceSelect.add(new Option('Select state / province', ''));

    provinces.forEach((province) => {
      const [value, label] = province;
      provinceSelect.add(new Option(label, value));
    });
  }

  function buildQueryParams(root) {
    const country = root.querySelector('#CartDrawerShippingCountry')?.value?.trim();
    const provinceWrap = root.querySelector('#CartDrawerShippingProvinceWrap');
    const province =
      provinceWrap && !provinceWrap.hidden
        ? root.querySelector('#CartDrawerShippingProvince')?.value?.trim()
        : '';
    const zip = root.querySelector('#CartDrawerShippingZip')?.value?.trim();

    if (!country || country === '---') {
      setStatus(root, 'Please choose a country.', true);
      return null;
    }

    if (!zip) {
      setStatus(root, 'Please enter a ZIP or postal code.', true);
      return null;
    }

    const params = new URLSearchParams();
    params.set('shipping_address[country]', country);
    params.set('shipping_address[zip]', zip);

    if (province) {
      params.set('shipping_address[province]', province);
    }

    return params;
  }

  function renderRates(root, rates) {
    const results = root.querySelector('#CartDrawerShippingResults');
    if (!results) return;

    results.innerHTML = '';

    if (!rates || !rates.length) {
      clearSelection(root, true);
      setStatus(root, 'No shipping rates were found for that address.', true);
      results.hidden = true;
      return;
    }

    const stored = readStoredSelection();
    let restoredSelection = null;

    rates.forEach((rate, index) => {
      const name = rate.presentment_name || rate.name || 'Shipping';
      const priceCents = toCents(rate.price);
      const rateId = `CartDrawerShippingRate-${index}`;

      const label = document.createElement('label');
      label.className = 'cart-shipping-estimator__rate-option';
      label.setAttribute('for', rateId);

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = 'CartDrawerShippingRate';
      input.id = rateId;

      const nameSpan = document.createElement('span');
      nameSpan.className = 'cart-shipping-estimator__rate-name';
      nameSpan.textContent = name;

      const priceSpan = document.createElement('span');
      priceSpan.className = 'cart-shipping-estimator__rate-price';
      priceSpan.textContent =
        priceCents === 0 ? 'Free' : formatMoneyFromCents(priceCents, getCurrency(root));

      input.addEventListener('change', () => {
        const selection = {
          name,
          priceCents,
          signature: currentSignature(root),
        };

        writeStoredSelection(selection);
        showSelectedShipping(root, selection);
        updateEstimatedTotal(root, priceCents);
      });

      if (selectionMatchesRate(stored, rate, root)) {
        input.checked = true;
        restoredSelection = {
          name,
          priceCents,
          signature: currentSignature(root),
        };
      }

      label.appendChild(input);
      label.appendChild(nameSpan);
      label.appendChild(priceSpan);
      results.appendChild(label);
    });

    results.hidden = false;
    setStatus(root, 'Choose a shipping option to update the estimated total.');

    if (restoredSelection) {
      showSelectedShipping(root, restoredSelection);
      updateEstimatedTotal(root, restoredSelection.priceCents);
    } else {
      clearSelection(root, false);
    }
  }

  async function calculateShipping(root) {
    const params = buildQueryParams(root);
    if (!params) return;

    clearResults(root);
    clearSelection(root, true);
    setStatus(root, 'Calculating shipping…');

    const button = root.querySelector('#CartDrawerShippingButton');
    if (button) button.disabled = true;

    const routesRoot = window.Shopify?.routes?.root || '/';
    const query = params.toString();

    try {
      const prepareUrl = `${routesRoot}cart/prepare_shipping_rates.json?${query}`;
      const asyncUrl = `${routesRoot}cart/async_shipping_rates.json?${query}`;

      const prepareResponse = await fetch(prepareUrl, {
        method: 'POST',
        headers: { Accept: 'application/json' },
      });

      if (!prepareResponse.ok) {
        throw new Error('Could not start shipping calculation.');
      }

      for (let attempt = 0; attempt < 10; attempt += 1) {
        const response = await fetch(asyncUrl, {
          headers: { Accept: 'application/json' },
        });

        if (!response.ok) {
          throw new Error('Could not retrieve shipping rates.');
        }

        const data = await response.json();

        if (data && Array.isArray(data.shipping_rates)) {
          renderRates(root, data.shipping_rates);
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, 700));
      }

      throw new Error('Shipping rates took too long to load. Please try again.');
    } catch (error) {
      clearResults(root);
      clearSelection(root, true);
      setStatus(root, error.message || 'Unable to calculate shipping.', true);
    } finally {
      if (button) button.disabled = false;
    }
  }

  function resetForAddressChange(root) {
    clearResults(root);
    clearSelection(root, true);
    setStatus(root, '');
  }

  function init(root) {
    if (!root || root.dataset.shippingEstimatorReady === 'true') return;

    root.dataset.shippingEstimatorReady = 'true';

    setDefaultCountry(root);
    renderProvinceOptions(root);

    const stored = readStoredSelection();
    if (!stored || stored.signature !== currentSignature(root)) {
      clearSelection(root, true);
    } else {
      showSelectedShipping(root, stored);
      updateEstimatedTotal(root, stored.priceCents);
    }
  }

  document.addEventListener('change', (event) => {
    if (event.target.matches('#CartDrawerShippingCountry')) {
      const root = event.target.closest(ROOT_SELECTOR);
      if (!root) return;

      renderProvinceOptions(root);
      resetForAddressChange(root);
    }

    if (
      event.target.matches('#CartDrawerShippingProvince') ||
      event.target.matches('#CartDrawerShippingZip')
    ) {
      const root = event.target.closest(ROOT_SELECTOR);
      if (!root) return;

      resetForAddressChange(root);
    }
  });

  document.addEventListener('click', (event) => {
    const button = event.target.closest('#CartDrawerShippingButton');
    if (!button) return;

    const root = button.closest(ROOT_SELECTOR);
    if (!root) return;

    calculateShipping(root);
  });

  document.addEventListener('keydown', (event) => {
    if (event.target.matches('#CartDrawerShippingZip') && event.key === 'Enter') {
      event.preventDefault();
      const root = event.target.closest(ROOT_SELECTOR);
      root?.querySelector('#CartDrawerShippingButton')?.click();
    }
  });

  document.addEventListener('DOMContentLoaded', () => {
    init(getRoot());
  });

  const observer = new MutationObserver(() => {
    init(getRoot());
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();